import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  Card,
  IconButton,
  Chip,
  LinearProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  ShoppingCart as ShoppingCartIcon,
  CalendarToday as CalendarTodayIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Restaurant as RestaurantIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { motion } from 'framer-motion';
import { useDailySales, useMonthToDateSales, useYearToDateSales } from '../hooks/useSalesData';
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';
import { useSalesTrend } from '../hooks/useSalesTrend';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';
import StatsCard from '../components/StatsCard';
import ProductMix from '../components/ProductMix';
import PaymentMethods from '../components/PaymentMethods';
import EmployeePerformance from '../components/EmployeePerformance';
import EnhancedStoreSelector from '../components/EnhancedStoreSelector';
import BrandOverview from '../components/BrandOverview';
import BrandDetailView from '../components/BrandDetailView';
import ComprehensiveMetrics from '../components/ComprehensiveMetrics';
import BrandComparison from '../components/BrandComparison';
import PredictiveInsights from '../components/PredictiveInsights';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd');
  const today = format(new Date(), 'yyyy-MM-dd');
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'overview' | 'detailed' | 'comparison'>('overview');
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '12m'>('7d');
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(yesterday); // Default to yesterday
  
  // Aggregate selected stores into a single identifier for API calls
  const aggregatedStoreId = selectedStores.length === 0 ? undefined : 
    selectedStores.length === 1 ? selectedStores[0] : selectedStores.join(',');
  
  // Fetch data using hooks
  const { data: dailySales, isLoading: isDailyLoading } = useDailySales(selectedDate, aggregatedStoreId);
  const { data: mtdSales, isLoading: isMtdLoading } = useMonthToDateSales(undefined, aggregatedStoreId);
  const { data: ytdSales, isLoading: isYtdLoading } = useYearToDateSales(undefined, aggregatedStoreId);
  
  // Fetch real dashboard metrics for current date
  const { data: dashboardMetrics, isLoading: isMetricsLoading } = useDashboardMetrics(undefined, aggregatedStoreId);

  // Fetch trend data for charts
  const { data: trendData, isLoading: isTrendLoading } = useSalesTrend(dateRange, aggregatedStoreId);

  // Chart data - use real trend API data
  const chartData = useMemo<Array<{date: string, sales: number, lastYearSales: number, orders: number, avgOrder: number}>>(() => {
    if (!trendData?.data) return [];
    return trendData.data;
  }, [trendData]);

  const hourlyData = useMemo<Array<{hour: number, sales: number, orders: number}>>(() => {
    // Only return data if we have real hourly API data
    // This would need an API endpoint for hourly sales data
    return [];
  }, [selectedStores]);

  const categoryData = useMemo<Array<{name: string, value: number, color: string}>>(() => {
    // Only return data if we have real category API data
    // This would need an API endpoint for category breakdown
    return [];
  }, [selectedStores]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            ...glassStyle,
            p: 2,
            borderRadius: '12px',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontWeight: 600 }}
            >
              {entry.name}: ${entry.value.toLocaleString()}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Handle brand detail view
  if (selectedBrand) {
    return <BrandDetailView brand={selectedBrand} onBack={() => setSelectedBrand(null)} />;
  }

  return (
    <Box>
      {/* Enhanced Header */}
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 3 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Multi-Store Analytics Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                {selectedStores.length === 0 
                  ? 'Select stores to view comprehensive analytics across your brands'
                  : selectedStores.length === 1
                  ? `Viewing data for 1 selected store`
                  : `Viewing aggregated data for ${selectedStores.length} selected stores`
                }
              </Typography>
              
              {selectedStores.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, newMode) => newMode && setViewMode(newMode)}
                    size="small"
                  >
                    <ToggleButton value="overview">Overview</ToggleButton>
                    <ToggleButton value="detailed">Detailed</ToggleButton>
                    <ToggleButton value="comparison">Compare</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <EnhancedStoreSelector
                selectedStores={selectedStores}
                onStoresChange={setSelectedStores}
              />
              
              {/* Date Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 'fit-content' }}>
                  Daily Data:
                </Typography>
                <ToggleButtonGroup
                  value={selectedDate}
                  exclusive
                  onChange={(e, newDate) => newDate && setSelectedDate(newDate)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.75rem',
                    }
                  }}
                >
                  <ToggleButton value={yesterday}>Yesterday</ToggleButton>
                  <ToggleButton value={today}>Today</ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  size="small"
                  InputProps={{
                    sx: {
                      '& input[type="date"]::-webkit-calendar-picker-indicator': {
                        filter: 'invert(1) !important',
                        WebkitFilter: 'invert(1) !important',
                        cursor: 'pointer',
                      }
                    }
                  }}
                  sx={{
                    width: 140,
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      fontSize: '0.75rem',
                    },
                    '& input::-webkit-calendar-picker-indicator': {
                      filter: 'invert(1) brightness(1.5) !important',
                      cursor: 'pointer',
                    }
                  }}
                />
              </Box>
              
              <ToggleButtonGroup
                value={dateRange}
                exclusive
                onChange={(e, newRange) => newRange && setDateRange(newRange)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 2,
                    py: 0.5,
                  }
                }}
              >
                <ToggleButton value="7d">7 Days</ToggleButton>
                <ToggleButton value="30d">30 Days</ToggleButton>
                <ToggleButton value="90d">90 Days</ToggleButton>
                <ToggleButton value="12m">12 Months</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* Brand Overview - Show when no stores selected or overview mode */}
      {(selectedStores.length === 0 || viewMode === 'overview') && (
        <BrandOverview 
          selectedStores={selectedStores} 
          onBrandClick={setSelectedBrand}
        />
      )}

      {/* Main Dashboard Content - Show when stores are selected */}
      {selectedStores.length > 0 && viewMode !== 'overview' && (
        <>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Today's Revenue"
            value={dashboardMetrics?.current?.totalSales || dashboardMetrics?.today?.sales || dailySales?.current?.totalSales || 0}
            prefix="$"
            change={dashboardMetrics?.comparisons?.dayOverDay?.salesChange || dailySales?.comparisons?.yearOverYear?.salesPercent}
            changeText="vs yesterday"
            icon={<AttachMoneyIcon />}
            delay={0}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Total Orders"
            value={dashboardMetrics?.current?.orderCount || dashboardMetrics?.today?.orders || dailySales?.current?.orderCount || 0}
            change={dashboardMetrics?.comparisons?.dayOverDay?.ordersChange || dailySales?.comparisons?.yearOverYear?.ordersPercent}
            changeText="vs yesterday"
            icon={<ReceiptIcon />}
            delay={0.1}
            color="secondary"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Average Order"
            value={dashboardMetrics?.current?.averageOrderValue || dashboardMetrics?.today?.averageOrderValue || dailySales?.current?.averageOrderValue || 0}
            prefix="$"
            icon={<ShoppingCartIcon />}
            delay={0.2}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="MTD Revenue"
            value={dashboardMetrics?.mtd?.sales || mtdSales?.current?.totalSales || 0}
            prefix="$"
            change={mtdSales?.yearOverYear?.salesPercent}
            changeText="vs same MTD last year"
            icon={<CalendarTodayIcon />}
            delay={0.3}
            color="error"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Sales Trend Chart */}
        <Grid item xs={12} lg={8}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card
              sx={{
                ...glassStyle,
                p: 3,
                height: 450,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Revenue Trend
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Sales performance with YoY comparison
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={dateRange}
                  exclusive
                  onChange={(_, newRange) => newRange && setDateRange(newRange)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(175, 35, 38, 0.2)',
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <ToggleButton value="7d">7D</ToggleButton>
                  <ToggleButton value="30d">30D</ToggleButton>
                  <ToggleButton value="90d">90D</ToggleButton>
                  <ToggleButton value="12m">12M</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              {isTrendLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '85%' }}>
                  <Typography variant="body2" color="text.secondary">Loading trend data...</Typography>
                </Box>
              )}
              {!isTrendLoading && chartData.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '85%' }}>
                  <Typography variant="body2" color="text.secondary">No trend data available</Typography>
                </Box>
              )}
              {!isTrendLoading && chartData.length > 0 && (
              <ResponsiveContainer width="100%" height="85%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#af2326" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#af2326" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="lastYearGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#666" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#666" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#A0AEC0"
                    style={{ fontSize: '0.75rem' }}
                  />
                  <YAxis 
                    stroke="#A0AEC0"
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="lastYearSales"
                    stroke="#666"
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    fillOpacity={1}
                    fill="url(#lastYearGradient)"
                    name="Last Year Sales"
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke="#af2326"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#salesGradient)"
                    name="Current Sales"
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    iconType="line"
                    wrapperStyle={{
                      paddingTop: '10px',
                      fontSize: '0.75rem'
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </Card>
          </motion.div>
        </Grid>

        {/* Category Breakdown */}
        <Grid item xs={12} lg={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.5 }}>
            <Card
              sx={{
                ...glassStyle,
                p: 3,
                height: 450,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Category Performance
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Sales distribution
                  </Typography>
                </Box>
                <Chip
                  label="Today"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(175, 35, 38, 0.2)',
                    color: '#5A8BFF',
                  }}
                />
              </Box>
              
              <ResponsiveContainer width="100%" height="60%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <Box sx={{ mt: 2 }}>
                {categoryData.map((category, index) => (
                  <Box
                    key={category.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '3px',
                          backgroundColor: category.color,
                        }}
                      />
                      <Typography variant="body2">{category.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {category.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Hourly Performance */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.6 }}>
            <Card
              sx={{
                ...glassStyle,
                p: 3,
                height: 400,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Hourly Performance
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Today's sales by hour
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: '#af2326' }} />
                    <Typography variant="caption">Sales</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: '#c94144' }} />
                    <Typography variant="caption">Orders</Typography>
                  </Box>
                </Box>
              </Box>

              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#A0AEC0"
                    style={{ fontSize: '0.75rem' }}
                    tickFormatter={(value) => `${value}:00`}
                  />
                  <YAxis 
                    stroke="#A0AEC0"
                    style={{ fontSize: '0.75rem' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="sales" fill="#af2326" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="orders" fill="#c94144" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </motion.div>
        </Grid>

        {/* New Analytics Section */}
        <Grid item xs={12} md={4}>
          <ProductMix date={yesterday} storeId={aggregatedStoreId} />
        </Grid>
        <Grid item xs={12} md={4}>
          <PaymentMethods date={yesterday} storeId={aggregatedStoreId} />
        </Grid>
        <Grid item xs={12} md={4}>
          <EmployeePerformance date={yesterday} storeId={aggregatedStoreId} />
        </Grid>

        {/* Store Comparison - Show when multiple stores selected */}
        {selectedStores.length > 1 && viewMode === 'comparison' && (
          <Grid item xs={12}>
            <BrandComparison selectedBrand="all" />
          </Grid>
        )}

        {/* Comprehensive YoY Metrics */}
        {viewMode === 'detailed' && (
          <Grid item xs={12}>
            <ComprehensiveMetrics selectedLocation={aggregatedStoreId || 'all'} />
          </Grid>
        )}

        {/* Predictive Insights */}
        {viewMode === 'detailed' && (
          <Grid item xs={12}>
            <PredictiveInsights selectedLocation={aggregatedStoreId || 'all'} />
          </Grid>
        )}

        {/* Performance Summary */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.7 }}>
            <Card
              sx={{
                ...glassStyle,
                p: 3,
                background: 'linear-gradient(135deg, rgba(175, 35, 38, 0.1) 0%, rgba(175, 35, 38, 0.05) 100%)',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Year-to-Date Summary
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Total Revenue
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      ${ytdSales?.current.totalSales ? ytdSales.current.totalSales.toLocaleString() : '0'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <ArrowUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                        12.5% from last year
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Total Orders
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {ytdSales?.current.totalOrders ? ytdSales.current.totalOrders.toLocaleString() : '0'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <ArrowUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                        8.3% from last year
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Average Order Value
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      ${ytdSales?.current.averageOrderValue ? ytdSales.current.averageOrderValue.toFixed(2) : '0.00'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <ArrowUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                      <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                        5.2% from last year
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Growth Score
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                      87/100
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={87} 
                      sx={{ 
                        mt: 1, 
                        height: 6, 
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          background: 'linear-gradient(90deg, #af2326 0%, #c94144 100%)',
                        }
                      }} 
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
        </>
      )}
    </Box>
  );
};

export default Dashboard;